import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import {
  approveChangeRequest,
  listChangeRequests,
  rejectChangeRequest,
  type ChangeRequest,
} from '../lib/api';
import { WafDataGrid } from '../components/WafDataGrid';

function prettyField(f: string) {
  return f.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const CONTAINER_ENTITY_CONTACT = 'contact';
const ENTITY_NOMINATION_IMAGE = 'nomination_image';
// Image-credit edits (text, not a file swap) — rendered as a normal before/after
// text diff, just with a clearer field label. (NOC-397)
const ENTITY_NOMINATION_IMAGE_CREDIT = 'nomination_image_credit';

// A stored image path is relative to the images root; the static handler serves
// it at /api/nomination_images/{relative}. Snapshots can also be JSON-quoted
// strings (older rows), so strip wrapping quotes defensively.
function imageUrl(rel: string | number | null | undefined): string | null {
  if (rel === null || rel === undefined) return null;
  let s = String(rel).trim();
  if (s === '' || s === 'null') return null;
  if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1);
  if (!s) return null;
  if (s.startsWith('http') || s.startsWith('/api/')) return s;
  return `/api/nomination_images/${s.replace(/^\/+/, '')}`;
}

function isImageRow(row: ChangeRequest): boolean {
  return row.target_entity_type === ENTITY_NOMINATION_IMAGE;
}

// A DELETE-variant image request: a nomination_image row whose proposed value is
// empty (no staged replacement) means "remove this slot", not "swap it".
function isImageDeleteRow(row: ChangeRequest): boolean {
  return isImageRow(row) && (row.proposed_value ?? '').trim() === '';
}

// An A2 board removal request: a nomination-field row on an a2board slot cleared
// to empty. Rendered with a clear "removal requested" treatment (not a blank swap).
function isA2BoardDeleteRow(row: ChangeRequest): boolean {
  return (
    row.target_entity_type !== ENTITY_NOMINATION_IMAGE &&
    (row.field === 'a2board1' || row.field === 'a2board2') &&
    (row.proposed_value ?? '').trim() === ''
  );
}

// Friendly names for contact-detail change requests (target_entity_type ===
// "contact"). Practice-name edits arrive as normal nomination rows on
// architect_company_name, so they're not listed here.
const CONTACT_FIELD_LABELS: Record<string, string> = {
  contact_name: 'Contact name',
  email: 'Email',
  phone: 'Telephone',
};

function fieldLabel(row: ChangeRequest): string {
  if (row.target_entity_type === CONTAINER_ENTITY_CONTACT) {
    return CONTACT_FIELD_LABELS[row.field] ?? prettyField(row.field);
  }
  if (isImageRow(row)) {
    // Field is the slot (hero, img2..img10). Flag deletions so admins can tell a
    // removal apart from a swap at a glance.
    return isImageDeleteRow(row) ? `Image removal: ${row.field}` : `Image: ${row.field}`;
  }
  if (row.target_entity_type === ENTITY_NOMINATION_IMAGE_CREDIT) {
    // Field is the slot; this is a credit (text) edit, not an image swap.
    return `Image credit: ${row.field}`;
  }
  return prettyField(row.field);
}

// Small before/after thumbnail pair for image change requests. Clicking a
// thumbnail opens the full image in a new tab.
// Thumbnail that opens the full image in a lightbox on click. Click the
// backdrop, the ✕, or press Esc to close. stopPropagation so opening it from a
// grid row doesn't also fire the row's onClick.
function ClickableImage({ src, alt, sx }: { src: string; alt: string; sx?: object }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box component="img" src={src} alt={alt}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        sx={{ cursor: 'zoom-in', ...sx }} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl"
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: { xs: 1, sm: 2 } } } }}>
        <Box sx={{ position: 'relative', lineHeight: 0 }} onClick={() => setOpen(false)}>
          <IconButton aria-label="Close image" onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
            <CloseIcon />
          </IconButton>
          <Box component="img" src={src} alt={alt}
            sx={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', display: 'block', borderRadius: 1 }} />
        </Box>
      </Dialog>
    </>
  );
}

// The "after"/proposed thumbnail source. For an APPROVED image request the
// pending file has been promoted to the live slot and the pending copy deleted,
// so proposed_value (the pending path) 404s — show the now-live slot image
// instead. Pending/rejected rows still point at proposed_value.
function proposedImageSrc(row: ChangeRequest): string | null {
  // A removal has no "after" image — the slot ends up empty whether it's still
  // pending or already approved (so don't fall through to the now-404 live URL).
  if (isImageDeleteRow(row)) return null;
  if (row.status === 'approved' && isImageRow(row)) {
    return imageUrl(`${row.target_entity_id}/${row.target_entity_id}_${row.field}.jpg`);
  }
  return imageUrl(row.proposed_value);
}

function ImageBeforeAfter({ row, size = 56, lightbox = false }: { row: ChangeRequest; size?: number; lightbox?: boolean }) {
  const before = imageUrl(row.current_value_snapshot);
  const after = proposedImageSrc(row);
  const isRemoval = isImageDeleteRow(row);
  const thumb = (src: string | null, alt: string) =>
    src ? (
      lightbox ? (
        <ClickableImage src={src} alt={alt}
          sx={{ width: size, height: size, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
      ) : (
      <Box
        component="a"
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'block', lineHeight: 0 }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            width: size, height: size, objectFit: 'cover', borderRadius: 1,
            border: '1px solid', borderColor: 'divider',
          }}
        />
      </Box>
      )
    ) : (
      <Box
        sx={{
          width: size, height: size, borderRadius: 1, border: '1px dashed',
          borderColor: 'divider', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'text.disabled',
        }}
      >
        <Typography variant="caption">none</Typography>
      </Box>
    );
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Stack alignItems="center" spacing={0.25}>
        <Typography variant="caption" color="text.secondary">Before</Typography>
        {thumb(before, 'current image')}
      </Stack>
      <Typography variant="body2" color="text.secondary">→</Typography>
      <Stack alignItems="center" spacing={0.25}>
        <Typography variant="caption" color="text.secondary">After</Typography>
        {isRemoval ? (
          <Box
            sx={{
              width: size, height: size, borderRadius: 1, border: '1px dashed',
              borderColor: 'error.main', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: 'error.main', p: 0.5, textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.1 }}>Removal requested</Typography>
          </Box>
        ) : (
          thumb(after, 'proposed image')
        )}
      </Stack>
    </Stack>
  );
}

function statusChip(status: string) {
  const colorMap: Record<string, 'warning' | 'success' | 'error'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
  };
  // Sentence case — "Pending / Approved / Rejected" rather than the
  // backend's lowercase enum.
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
  return <Chip label={label} size="small" color={colorMap[status] || 'default'} />;
}

interface Row extends ChangeRequest { id: number }

export function ChangeRequests() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewDialog, setReviewDialog] = useState<{ cr: Row; action?: 'approve' | 'reject' } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const reload = () => {
    setLoading(true);
    listChangeRequests()
      .then((data) => { setRows(data as Row[]); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  };

  useEffect(reload, []);

  const handleReview = async (action: 'approve' | 'reject') => {
    if (!reviewDialog) return;
    setProcessing(true);
    try {
      if (action === 'approve') {
        await approveChangeRequest(reviewDialog.cr.id, reviewNotes);
      } else {
        await rejectChangeRequest(reviewDialog.cr.id, reviewNotes);
      }
      setReviewDialog(null);
      setReviewNotes('');
      reload();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Review failed');
    } finally {
      setProcessing(false);
    }
  };

  const columns: GridColDef<Row>[] = [
    // Narrow fixed widths on the short/predictable columns (IDs, dates,
    // labels). Wider on free-text columns where content length varies
    // (title / old value / proposed value).
    {
      // A bit of a bodge: contact-detail change requests aren't tied to a
      // project, so surface WHO submitted it (the architect) with a link
      // through to their record on the Users page, pre-filtered by ?q=.
      field: 'submitter_name', headerName: 'Architect', width: 180,
      renderCell: (p: GridRenderCellParams<Row>) => {
        const name = p.row.submitter_name || p.row.submitter_email || '—';
        const key = p.row.submitter_email || p.row.submitter_name || '';
        if (!key) return <Typography variant="body2">{name}</Typography>;
        return (
          <Link component={RouterLink} to={`/users?q=${encodeURIComponent(key)}`}
            onClick={(e) => e.stopPropagation()} underline="hover"
            sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}
            title={`View ${name} on the Users page`}>
            {name}
          </Link>
        );
      },
    },
    {
      field: 'project_title', headerName: 'Project Title', width: 240,
      renderCell: (p: GridRenderCellParams<Row>) => {
        // Contact-detail requests aren't tied to a nomination, so there's
        // no project to link to — show a static label instead.
        if (p.row.target_entity_type === CONTAINER_ENTITY_CONTACT) {
          return (
            <Typography variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Contact details
            </Typography>
          );
        }
        return (
          <Link component={RouterLink} to={`/entry/${p.row.target_entity_id}`}
            onClick={(e) => e.stopPropagation()} underline="hover"
            sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}
            title={p.row.project_title || p.row.nomination_code || p.row.target_entity_id}>
            {p.row.project_title || p.row.nomination_code || p.row.target_entity_id}
          </Link>
        );
      },
    },
    {
      field: 'field', headerName: 'Field Changed', width: 160,
      valueGetter: (_v, row) => fieldLabel(row),
    },
    {
      // Current value snapshot taken at submit time — the value as it was
      // BEFORE the requested change.
      field: 'current_value_snapshot', headerName: 'Changed From', width: 220,
      renderCell: (p: GridRenderCellParams<Row>) => {
        // Image rows show a before/after thumbnail pair in the proposed column
        // instead — keep this cell light to avoid a duplicate "before" image.
        if (isImageRow(p.row)) {
          return <Typography variant="body2" color="text.secondary">(see preview →)</Typography>;
        }
        const v = p.row.current_value_snapshot;
        const text = v === null || v === undefined || v === '' ? '(empty)' : String(v);
        return (
          <Typography variant="body2" color={v === null || v === undefined || v === '' ? 'text.disabled' : 'text.primary'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>
            {text}
          </Typography>
        );
      },
    },
    {
      field: 'proposed_value', headerName: 'Proposed Change', width: 240,
      renderCell: (p: GridRenderCellParams<Row>) => {
        if (isImageRow(p.row)) {
          return <ImageBeforeAfter row={p.row} />;
        }
        if (isA2BoardDeleteRow(p.row)) {
          return (
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
              Removal requested
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={p.row.proposed_value}>
            {p.row.proposed_value}
          </Typography>
        );
      },
    },
    {
      field: 'created_at', headerName: 'Submitted', width: 160,
      valueGetter: (_v, row) => new Date(row.created_at),
      renderCell: (p: GridRenderCellParams<Row>) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(p.row.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'actions', headerName: 'Reviewed By', width: 200, sortable: false,
      renderCell: (p: GridRenderCellParams<Row>) => {
        if (p.row.status !== 'pending') {
          return (
            <Typography variant="caption" color="text.secondary">
              {p.row.reviewed_by} · {p.row.reviewed_at ? new Date(p.row.reviewed_at).toLocaleDateString() : ''}
            </Typography>
          );
        }
        return (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
              onClick={(e) => { e.stopPropagation(); setReviewDialog({ cr: p.row, action: 'approve' }); }}>
              Approve
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />}
              onClick={(e) => { e.stopPropagation(); setReviewDialog({ cr: p.row, action: 'reject' }); }}>
              Reject
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'status', headerName: 'Status', width: 120,
      renderCell: (p: GridRenderCellParams<Row>) => statusChip(p.row.status),
    },
  ];

  // Paper width matches the sum of column widths so the table is exactly
  // as wide as its content — no trailing whitespace on big screens. Cap
  // at 90vw so it stays readable on narrower ones (horizontal scroll
  // inside the grid kicks in past the cap).
  const tableWidth = columns.reduce((sum, c) => sum + (c.width ?? 100), 0) + 2;

  return (
    <Stack spacing={3} sx={{ px: { xs: 0, sm: 0 } }}>
      <Typography variant="h3" component="h1" align="center">
        Change requests
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper
        sx={{
          // Content-width (sum of columns), centred on the viewport
          // rather than the Layout's 1200px wrapper — when the table is
          // wider than that wrapper, `mx: auto` doesn't reach far enough
          // and the table appears offset right. position+translate
          // centres it regardless. Capped at 90vw so it never overflows
          // the screen.
          //
          // On xs the fixed content-width + translate trick would push the
          // grid off-screen, so drop it: let the Paper be full-width and
          // let the DataGrid scroll its own columns horizontally.
          position: { xs: 'static', sm: 'relative' },
          left: { xs: 'auto', sm: '50%' },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          width: { xs: '100%', sm: tableWidth },
          maxWidth: { xs: '100%', sm: '90vw' },
        }}
      >
        <WafDataGrid<Row>
          rows={rows}
          columns={columns}
          loading={loading}
          density="standard"
          onRowClick={(p) => setReviewDialog({ cr: p.row })}
          pageSizeOptions={[25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Paper>

      {/* Change request detail + review */}
      <Dialog
        open={!!reviewDialog}
        onClose={() => { setReviewDialog(null); setReviewNotes(''); }}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>Change request detail</DialogTitle>
        <DialogContent dividers>
          {reviewDialog && (() => {
            const cr = reviewDialog.cr;
            const pending = cr.status === 'pending';
            const isContact = cr.target_entity_type === CONTAINER_ENTITY_CONTACT;
            const submitterKey = cr.submitter_email || cr.submitter_name || '';
            return (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {/* Summary */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 4 }} useFlexGap flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Architect</Typography>
                    {submitterKey ? (
                      <Link component={RouterLink} to={`/users?q=${encodeURIComponent(submitterKey)}`}
                        underline="hover" sx={{ color: 'text.primary' }}>
                        {cr.submitter_name || cr.submitter_email}
                      </Link>
                    ) : (
                      <Typography>—</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Entry</Typography>
                    <Typography>{isContact ? 'Contact details' : (cr.project_title || cr.target_entity_id)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Field</Typography>
                    <Typography>{fieldLabel(cr)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Submitted</Typography>
                    <Typography>{new Date(cr.created_at).toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    {statusChip(cr.status)}
                  </Box>
                </Stack>

                <Divider />

                {/* Original -> Proposed */}
                {isImageRow(cr) ? (
                  <ImageBeforeAfter row={cr} size={240} lightbox />
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Original</Typography>
                      <Typography sx={{ textDecoration: 'line-through', color: 'text.disabled', wordBreak: 'break-word' }}>
                        {String(cr.current_value_snapshot ?? '') || '(empty)'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Proposed</Typography>
                      {isA2BoardDeleteRow(cr) ? (
                        <Typography color="error.main" sx={{ fontWeight: 600 }}>Removal requested</Typography>
                      ) : (
                        <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{cr.proposed_value}</Typography>
                      )}
                    </Box>
                  </Stack>
                )}

                {cr.reason && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Reason given</Typography>
                    <Typography variant="body2">{cr.reason}</Typography>
                  </Box>
                )}

                {!pending && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Reviewed</Typography>
                    <Typography variant="body2">
                      {(cr.reviewed_by || 'unknown')}{cr.reviewed_at ? ` · ${new Date(cr.reviewed_at).toLocaleString()}` : ''}
                    </Typography>
                    {cr.review_notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{cr.review_notes}</Typography>}
                  </Box>
                )}

                {pending && (
                  <TextField
                    fullWidth multiline minRows={2} size="small"
                    label="Notes (optional) — these notes are sent to the entrant upon approval/rejection"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setReviewDialog(null); setReviewNotes(''); }} disabled={processing}>Close</Button>
          {reviewDialog?.cr.status === 'pending' && (
            <>
              <Button variant="outlined" color="error" startIcon={<CloseIcon />}
                onClick={() => handleReview('reject')} disabled={processing}>
                {processing ? 'Processing…' : 'Reject'}
              </Button>
              <Button variant="contained" color="success" startIcon={<CheckIcon />}
                onClick={() => handleReview('approve')} disabled={processing}>
                {processing ? 'Processing…' : 'Approve & apply'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
